import isURL from 'validator/lib/isURL';
import { reportError } from '../lib/errors';
import { ROUTES } from '../config/Nav';

/**
 * Encodes a URL to base64 after validating it belongs to the ELEVATOR origin.
 * Validates that the URL matches the allowed host whitelist before encoding.
 *
 * @param fromURL - The URL to encode (must be a valid ELEVATOR domain URL)
 * @returns Base64 encoded URL string, or null if validation or encoding fails
 */
export const encodeFromURL = (fromURL: string): string | null => {
  try {
    if (!fromURL) {
      throw new Error('from URL must not be null or empty');
    }

    if (!window) {
      throw new Error('window must be defined to B64 encode fromURL');
    }

    const hostWhitelist = [
      process.env.COOKIE_DOMAIN,
      ...(process.env.COOKIE_DOMAIN === 'elevatormag.com' ? ['www.elevatormag.com'] : []),
    ];

    // determine if string is a valid url
    const isValidURL = isURL(fromURL, {
      require_host: true,
      require_protocol: true,
      host_whitelist: hostWhitelist,
      require_tld: process.env.COOKIE_DOMAIN !== 'localhost',
    });

    // Invalidate redirect if not valid
    if (!isValidURL) {
      throw new Error(
        `invalid redirect URL. Must be of origin ELEVATOR, but got ${fromURL}`,
      );
    }

    // Parse redirect to b64 string
    const b64EncodedFromURL = window.btoa(fromURL);

    // Otherwise return the requested redirect
    return b64EncodedFromURL;
  } catch (err) {
    reportError(err, {
      metaData: {
        operation: 'b64 encode redirect URL',
      },
    });
    return null;
  }
};

/**
 * Builds the login redirect URL with optional return path
 * @param returnPath - The path to return to after login (e.g., '/account/settings')
 * @returns The login URL with optional 'from' query parameter
 */
export const buildLoginRedirectUrl = (returnPath?: string): string => {
  if (!returnPath) {
    return ROUTES.AUTH.LOG_IN.to;
  }

  // Build full URL for encoding
  const fullUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${returnPath}`
      : `${process.env.NEXT_PUBLIC_SITE_URL || 'https://elevatormag.com'}${returnPath}`;

  const b64EncodedFromURL = encodeFromURL(fullUrl);

  if (b64EncodedFromURL) {
    return `${ROUTES.AUTH.LOG_IN.to}?from=${b64EncodedFromURL}`;
  }

  return ROUTES.AUTH.LOG_IN.to;
};
